import { escapeRegExp, first, isEmpty } from 'lodash';
import {
  ICCAsset,
  IProduct,
  IProductVariation,
  IProductVariationFlavor,
  IProductVariationSize,
} from 'src/interfaces';
import { lang, normalizeSearchKeywordsBackslashes } from 'src/utils';
import { getProductNameFromAssetId } from 'src/utils/gcf-helper';

export const PRODUCT_MODAL_PAGE_SIZE = 10;

export const SEARCH_TYPES = {
  productCode: 'productCode',
  productName: 'productName',
  productVariation: 'productVariation',
} as const;

export type SearchType = (typeof SEARCH_TYPES)[keyof typeof SEARCH_TYPES];

export type SearchTermsByType = Partial<Record<SearchType, string>>;

export const searchTypeOptions = [
  { value: SEARCH_TYPES.productCode, label: lang.productCode },
  { value: SEARCH_TYPES.productName, label: lang.productName },
  { value: SEARCH_TYPES.productVariation, label: lang.productVariation },
];

// Show only products and variations that are currently active, and exclude
// products whose associated variations are all inactive.
const activeProductFilters = [
  'active=true',
  'productVariations.active=true',
  'has_active_variation=true',
];

// Builds the viewProductsWithVariations query params used by the modal.
export const getProductListQueryFilters = ({
  filters = [],
  isExactProductNameSearch,
  searchTermsByType,
}: {
  filters?: string[];
  isExactProductNameSearch: boolean;
  searchTermsByType: SearchTermsByType;
}) => {
  const queryFilters = [
    'sort=product_code',
    ...activeProductFilters,
    ...filters,
    `pageSize=${PRODUCT_MODAL_PAGE_SIZE}`,
  ];

  if (isEmpty(searchTermsByType)) {
    return queryFilters;
  }

  const productCodeSearchValue = searchTermsByType[SEARCH_TYPES.productCode];
  const productNameSearchValue = searchTermsByType[SEARCH_TYPES.productName];
  const productVariationSearchValue =
    searchTermsByType[SEARCH_TYPES.productVariation];

  if (productCodeSearchValue) {
    queryFilters.push(
      `productVariations.product_code.regexp=${encodeURIComponent(
        escapeRegExp(productCodeSearchValue),
      )}`,
    );
  }

  if (productNameSearchValue) {
    queryFilters.push(
      isExactProductNameSearch
        ? `display_name.list=${encodeURIComponent(productNameSearchValue)}`
        : `display_name.filter=${encodeURIComponent(
            normalizeSearchKeywordsBackslashes(
              productNameSearchValue,
            ) as string,
          )}`,
    );
  }

  if (productVariationSearchValue) {
    queryFilters.push(
      `productVariations.name.regexp=${encodeURIComponent(
        normalizeSearchKeywordsBackslashes(
          productVariationSearchValue,
        ) as string,
      )}`,
    );
  }

  return queryFilters;
};
const getFirstActiveVariationByCreationOrder = (
  productVariations?: IProductVariation[],
) =>
  [...(productVariations ?? [])]
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )
    .find((variation) => variation.active);

// Gets the best available product name for display.
export const getProductDisplayName = (product: IProduct, assets: ICCAsset[]) =>
  product?.name_override ||
  product?.product_name ||
  product?.productVariations?.find(
    (variation) => variation.product_code === product.product_code,
  )?.name ||
  getFirstActiveVariationByCreationOrder(product?.productVariations)?.name ||
  getProductNameFromAssetId(assets, first(product.asset_id)) ||
  `Product #${product?.product_code}`;

export const getMatchingVariationName = (
  product: IProduct,
  searchTerm?: string,
) => {
  const normalizedSearchTerm = searchTerm?.trim().toLowerCase();

  if (!normalizedSearchTerm) {
    return undefined;
  }

  return product.productVariations?.find((variation) =>
    variation.name?.toLowerCase().includes(normalizedSearchTerm),
  )?.name;
};

// Displays the variation details in the format product code, variation type, and flavour.
export const getVariationLabel = (
  variation: IProductVariation,
  productVariationTypes: IProductVariationSize[] = [],
  productVariationFlavors: IProductVariationFlavor[] = [],
) => {
  const sizeName =
    variation.productVariationSize?.name ||
    variation.productVariantName ||
    productVariationTypes.find(
      (item) => item.id === variation.product_variation_size_id,
    )?.name ||
    '';
  const flavorName =
    variation.productVariationFlavor?.name ||
    variation.productFlavorName ||
    productVariationFlavors.find(
      (item) => item.id === variation.product_variation_flavor_id,
    )?.name ||
    '';
  const variationDetails = [sizeName, flavorName].filter(Boolean).join(' ');

  return [variation.product_code, variationDetails].filter(Boolean).join(' ');
};

export const getProductsWithSizeAndFlavor = (
  products: IProduct[],
  productVariationTypes: IProductVariationSize[],
  productVariationFlavors: IProductVariationFlavor[],
) => {
  if (!products.length) return [];

  return products.map((product) => ({
    ...product,
    productVariations: product.productVariations.map((variation) => ({
      ...variation,
      productVariationSize: productVariationTypes.find(
        (type) => type.id === variation.product_variation_size_id,
      ),
      productVariationFlavor: productVariationFlavors.find(
        (flavor) => flavor.id === variation.product_variation_flavor_id,
      ),
    })),
  }));
};

export const getProductsWithAssets = (
  products: IProduct[],
  assets: ICCAsset[],
) =>
  products.map((product) => {
    const productAssets = assets.filter((asset) =>
      product.asset_id?.some((assetId) => String(assetId) === String(asset.id)),
    );

    return {
      ...product,
      ...(productAssets.length ? { assets: productAssets } : {}),
      productVariations: product.productVariations.map((variation) => {
        const variationAssets = assets.filter((asset) =>
          variation.asset_id?.some(
            (assetId) => String(assetId) === String(asset.id),
          ),
        );

        return {
          ...variation,
          ...(variationAssets.length ? { assets: variationAssets } : {}),
        };
      }),
    };
  });
